import './AddMultiPage.scss';
import { Button, Card, Dropdown, Icon, Input, LabelValue, Textarea, Typography } from '@design-system/index';
import { Formik } from 'formik';
import { MemberTable } from '@services/member/member/member.types';
import { MembersListLine } from '@icons';
import { useAddMultiPageVM } from './useAddMultiPageVM';
import { AsyncDropdown, DropdownOption } from '@design-system/components/Dropdown/AsyncDropdown';
import { useGetMembersMutation } from '@services/member/member/member.endpoints';
import { formatMoney } from '@utils/index';

export default function AddPage() {
    const { 
        submitForm, 
        isLoading, 
        ticketTypes, 
        member,
        ticketBlocks,
        addTicketBlock,
        removeTicketBlock,
        updateTicketBlock
    } = useAddMultiPageVM();

    return (
        <div className="add-member-container">
            <Card
                isLoading={isLoading}
                headerType="full"
                showBack
                headerTitle={'Nouvelle vente billetterie'}
                headerIcon={<Icon icon={<MembersListLine />} size={20} color="primary" />}
            >
                <>
                    <Formik
                        initialValues={
                           {
                                member: member
                                    ? {
                                        id: member.id.toString(),
                                        label: member.cosemNumber + ' - ' + member.fullname
                                    }
                                    : null,
                                ticketingAt: new Date().toISOString().split('T')[0]
                            }
                        }
                        enableReinitialize
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        onSubmit={async (values: { member: any; ticketingAt: string }) => {
                            await submitForm(values);
                        }}
                    >
                        {({ values, handleChange, handleSubmit, setFieldValue, isSubmitting }) => {
                            return (
                                <form onSubmit={handleSubmit} className="form">
                                    <div className="form-section-container">
                                        <div className="form-section">
                                            <Typography intrinsic="h3">Informations générales</Typography>
                                            <div className="form-section-fields">
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <AsyncDropdown<MemberTable>
                                                            label="Rechercher un adhérent"
                                                            required
                                                            onOptionChange={(option) => {
                                                                setFieldValue('member', option);
                                                            }}
                                                            mutationHook={useGetMembersMutation}
                                                            mapDataToOptions={(data) =>
                                                                data.map((item) => ({
                                                                    id: item.id.toString(),
                                                                    label:
                                                                        item.cosemNumber +
                                                                        ' - ' +
                                                                        item.personalInformation.lastname +
                                                                        ' ' +
                                                                        item.personalInformation.firstname
                                                                }))
                                                            }
                                                            value={
                                                                values.member
                                                                    ? 'label' in values.member
                                                                        ? (values.member as unknown as DropdownOption)
                                                                        : {
                                                                              id: values.member.id?.toString() || '',
                                                                              label:
                                                                                  (values.member.cosemNumber || '') +
                                                                                  ' - ' +
                                                                                  (values.member.fullname || '')
                                                                          }
                                                                    : undefined
                                                            }
                                                            isSearchable
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <Input
                                                            type="date"
                                                            required
                                                            name="ticketingAt"
                                                            label="Date de la vente"
                                                            value={values.ticketingAt}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="form-section">
                                            <Typography intrinsic="h3">
                                                Billets à vendre
                                                <Button 
                                                    style={{ marginTop: '12px' }}
                                                        type="button" 
                                                        variant="secondary" 
                                                        onClick={addTicketBlock}
                                                    >
                                                Ajouter une vente
                                            </Button>
                                            </Typography>

                                            <div className="form-section-fields">
                                            {ticketBlocks.map((block, index) => (
                                                
                                                    <div className="form-section-fields" key={block.id} style={{ width: '100%' }}>
                                                        <div className="ticket-block-header">
                                                            <Typography intrinsic="h4" flex>
                                                                {ticketBlocks.length > 1 && (
                                                                    <Button 
                                                                        type="button" 
                                                                        variant="danger" 
                                                                        onClick={() => removeTicketBlock(block.id)}
                                                                    >
                                                                        Supprimer
                                                                    </Button>
                                                                )}
                                                                Vente billets #{ticketBlocks.length - index}
                                                            </Typography>
                                                        </div>

                                                        <div className="form-line">
                                                            <div className="form-group">
                                                                <Dropdown
                                                                    label="Type de billet"
                                                                    required
                                                                    isSearchable
                                                                    value={block.ticketType || null}
                                                                    options={ticketTypes.map((type) => ({ id: type.id, label: type.label }))}
                                                                    onOptionChange={(option) => {
                                                                        updateTicketBlock(block.id, 'ticketType', option);
                                                                        const selectedType = ticketTypes.find((t) => t.id === option.id);
                                                                        if (selectedType?.amount) {
                                                                            updateTicketBlock(block.id, 'amount', selectedType.amount);
                                                                        }
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                        
                                                        <div className="form-line">
                                                            <div className="form-group">
                                                                <Input
                                                                    name={`firstTicketNumber-${block.id}`}
                                                                    label="Numéro du premier billet"
                                                                    value={block.firstTicketNumber}
                                                                    required
                                                                    type="number"
                                                                    onChange={(e) => updateTicketBlock(block.id, 'firstTicketNumber', Number(e.target.value))}
                                                                />
                                                            </div>
                                                            <div className="form-group">
                                                                <Input
                                                                    name={`lastTicketNumber-${block.id}`}
                                                                    label="Numéro du dernier billet"
                                                                    value={block.lastTicketNumber}
                                                                    required
                                                                    type="number"
                                                                    onChange={(e) => updateTicketBlock(block.id, 'lastTicketNumber', Number(e.target.value))}
                                                                />
                                                            </div>
                                                            <div className="form-group">
                                                                <Input
                                                                    name={`amount-${block.id}`}
                                                                    type="number"
                                                                    label="Prix du billet"
                                                                    rightContent={'€'}
                                                                    value={block.amount}
                                                                    required
                                                                    onChange={(e) => updateTicketBlock(block.id, 'amount', Number(e.target.value))}
                                                                />
                                                            </div>
                                                        </div>
                                                        
                                                        <div className="form-line bordered">
                                                            <div className="form-group">
                                                                <LabelValue 
                                                                    label="Nombre de billets" 
                                                                    value={(Number(block.lastTicketNumber) - Number(block.firstTicketNumber)) > 0 ? Number(block.lastTicketNumber) - Number(block.firstTicketNumber) + 1 : 0} 
                                                                />
                                                            </div>
                                                            <div className="form-group">
                                                                <LabelValue
                                                                    label="Montant total"
                                                                    value={formatMoney(Number(block.amount) * (Number(block.lastTicketNumber) - Number(block.firstTicketNumber) + 1))}
                                                                />
                                                            </div>
                                                        </div>
                                                        
                                                        <div className="form-line">
                                                            <div className="form-group">
                                                                <Textarea
                                                                    name={`observation-${block.id}`}
                                                                    label="Commentaire pour ce bloc"
                                                                    value={block.observation}
                                                                    height={100}
                                                                    onChange={(e) => {
                                                                        const value = typeof e === 'string' ? e : e.target.value;
                                                                        updateTicketBlock(block.id, 'observation', value);
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="action-buttons">
                                        <Button type="submit" disabled={isSubmitting} variant="primary">
                                            Valider la création
                                        </Button>
                                    </div>
                                </form>
                            );
                        }}
                    </Formik>
                </>
            </Card>
        </div>
    );
}
